import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getInstitutions,
  createInstitution,
  getDepartments,
  getTerms,
  getTermCapacities,
  createTermCapacity,
  updateTermCapacity,
  getDepartmentTermCapacities,
  createDepartmentTermCapacity,
  updateDepartmentTermCapacity,
  deleteTermCapacity,
  deleteDepartmentTermCapacity,
} from '../services/api';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const numberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const AdminCapacityManager = () => {
  const [activeTab, setActiveTab] = useState('term'); // 'term' or 'dept'
  const [institutions, setInstitutions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [terms, setTerms] = useState([]);
  const [termCaps, setTermCaps] = useState([]);
  const [deptCaps, setDeptCaps] = useState([]);
  const [termDrafts, setTermDrafts] = useState({});
  const [deptDrafts, setDeptDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [savingTerm, setSavingTerm] = useState(false);
  const [savingDept, setSavingDept] = useState(false);
  const [message, setMessage] = useState(null);
  const [newInstitution, setNewInstitution] = useState({ name: '', short_name: '', is_rural: false });
  const [newTermBatch, setNewTermBatch] = useState([{ id: 'init', institution_id: '', caps: {} }]);
  const [newDeptBatch, setNewDeptBatch] = useState([{ id: 'init', institution_id: '', department_id: '', caps: {} }]);

  // LocalStorage から初期値を読み込む
  const [checkedTermRows, setCheckedTermRows] = useState(() => {
    try {
      const saved = localStorage.getItem('crikura_admin_checked_term_rows');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [checkedDeptRows, setCheckedDeptRows] = useState(() => {
    try {
      const saved = localStorage.getItem('crikura_admin_checked_dept_rows');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // チェック状態が変更されたら LocalStorage に保存
  useEffect(() => {
    localStorage.setItem('crikura_admin_checked_term_rows', JSON.stringify([...checkedTermRows]));
  }, [checkedTermRows]);

  useEffect(() => {
    localStorage.setItem('crikura_admin_checked_dept_rows', JSON.stringify([...checkedDeptRows]));
  }, [checkedDeptRows]);

  const showMessage = useCallback((text, tone = 'info') => {
    setMessage({ text, tone });
    setTimeout(() => setMessage(null), 3000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [inst, dept, termList, termCapsRes, deptCapsRes] = await Promise.all([
        getInstitutions(),
        getDepartments(),
        getTerms(),
        getTermCapacities(0, 2000),
        getDepartmentTermCapacities().catch((err) => {
          if (err?.response?.status === 404) return [];
          throw err;
        }),
      ]);
      setInstitutions(inst);
      setDepartments(dept);
      setTerms(termList);
      setTermCaps(termCapsRes);
      setDeptCaps(deptCapsRes);
      setTermDrafts({});
      setDeptDrafts({});
      // リロードでリセットしないため、ここではクリアしない
      // setCheckedTermRows(new Set());
      // setCheckedDeptRows(new Set());
      setInitialized(true);
    } catch (err) {
      console.error(err);
      showMessage('データの取得に失敗しました。', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const instMap = useMemo(() => Object.fromEntries(institutions.map((inst) => [inst.id, inst])), [institutions]);
  const deptMap = useMemo(() => Object.fromEntries(departments.map((dept) => [dept.id, dept])), [departments]);
  const termMap = useMemo(() => Object.fromEntries(terms.map((term) => [term.id, term])), [terms]);

  const getInstitutionName = useCallback(
    (institutionId) => {
      const inst = instMap[institutionId];
      return inst?.name || inst?.official_name || '';
    },
    [instMap]
  );

  const getDepartmentName = useCallback(
    (departmentId) => {
      const dept = deptMap[departmentId];
      return dept?.name || dept?.official_name || '';
    },
    [deptMap]
  );

  const termRows = useMemo(
    () =>
      [...termCaps].sort((a, b) => {
        const an = getInstitutionName(a.institution_id);
        const bn = getInstitutionName(b.institution_id);
        if (an === bn) return a.term_id - b.term_id;
        return an.localeCompare(bn, 'ja');
      }),
    [termCaps, getInstitutionName]
  );

  const deptMatrix = useMemo(() => {
    const groups = {};
    deptCaps.forEach((cap) => {
      const key = `${cap.institution_id}-${cap.department_id}`;
      if (!groups[key]) {
        groups[key] = {
          institution_id: cap.institution_id,
          department_id: cap.department_id,
          caps: {},
        };
      }
      groups[key].caps[cap.term_id] = cap;
    });
    return Object.values(groups).sort((a, b) => {
      const an = getInstitutionName(a.institution_id);
      const bn = getInstitutionName(b.institution_id);
      if (an === bn) {
        const ad = getDepartmentName(a.department_id);
        const bd = getDepartmentName(b.department_id);
        return ad.localeCompare(bd, 'ja');
      }
      return an.localeCompare(bn, 'ja');
    });
  }, [deptCaps, getInstitutionName, getDepartmentName]);

  const toggleTermRowCheck = (id) => {
    setCheckedTermRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleDeptRowCheck = (key) => {
    setCheckedDeptRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDeleteTermRow = async (id) => {
    if (!window.confirm('この行を削除しますか？')) return;
    try {
      await deleteTermCapacity(id);
      showMessage('削除しました', 'success');
      await loadData();
    } catch (err) {
      console.error(err);
      showMessage('削除に失敗しました', 'error');
    }
  };

  const handleDeleteDeptRow = async (row) => {
    if (!window.confirm('この行（病院×診療科）の全タームの定員データを削除しますか？')) return;
    try {
      const idsToDelete = Object.values(row.caps).map((c) => c.id);
      await Promise.all(idsToDelete.map((id) => deleteDepartmentTermCapacity(id)));
      showMessage('削除しました', 'success');
      await loadData();
    } catch (err) {
      console.error(err);
      showMessage('削除に失敗しました', 'error');
    }
  };

  const handleBulkDeleteTermRows = async () => {
    if (!checkedTermRows.size) return;
    if (!window.confirm(`選択した ${checkedTermRows.size} 件の行を削除しますか？`)) return;

    setLoading(true);
    try {
      await Promise.all([...checkedTermRows].map((id) => deleteTermCapacity(id)));
      showMessage(`${checkedTermRows.size} 件を削除しました`, 'success');
      setCheckedTermRows(new Set());
      await loadData();
    } catch (err) {
      console.error(err);
      showMessage('削除に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeleteDeptRows = async () => {
    if (!checkedDeptRows.size) return;
    if (!window.confirm(`選択した ${checkedDeptRows.size} 件の行（病院×診療科）の全タームデータを削除しますか？`)) return;

    setLoading(true);
    try {
      const idsToDelete = [];
      deptCaps.forEach((cap) => {
        const key = `${cap.institution_id}-${cap.department_id}`;
        if (checkedDeptRows.has(key)) {
          idsToDelete.push(cap.id);
        }
      });

      await Promise.all(idsToDelete.map((id) => deleteDepartmentTermCapacity(id)));
      showMessage(`${checkedDeptRows.size} 行（計 ${idsToDelete.length} 件）を削除しました`, 'success');
      setCheckedDeptRows(new Set());
      await loadData();
    } catch (err) {
      console.error(err);
      showMessage('削除に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTermDraft = (id, field, value) => {
    setTermCaps((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
    setTermDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleDeptDraft = (id, value) => {
    setDeptCaps((prev) => prev.map((c) => (c.id === id ? { ...c, capacity: value } : c)));
    setDeptDrafts((prev) => ({ ...prev, [id]: { capacity: value } }));
  };

  const saveTerms = async () => {
    const ids = Object.keys(termDrafts);
    if (!ids.length) return showMessage('変更はありません。', 'info');
    setSavingTerm(true);
    try {
      await Promise.all(
        ids.map(async (id) => {
          const cap = termCaps.find((c) => c.id === Number(id));
          if (!cap) return;
          await updateTermCapacity(Number(id), {
            institution_id: cap.institution_id,
            term_id: cap.term_id,
            total_capacity: numberOrNull(cap.total_capacity),
            male_capacity: numberOrNull(cap.male_capacity),
            female_capacity: numberOrNull(cap.female_capacity),
            stay_capacity: numberOrNull(cap.stay_capacity),
            is_same_sex_only: cap.is_same_sex_only,
          });
        })
      );
      showMessage('病院×タームの定員を保存しました。', 'success');
      await loadData();
    } catch (err) {
      console.error(err);
      showMessage('保存に失敗しました。', 'error');
    } finally {
      setSavingTerm(false);
    }
  };

  const saveDept = async () => {
    const ids = Object.keys(deptDrafts);
    if (!ids.length) return showMessage('変更はありません。', 'info');
    setSavingDept(true);
    try {
      await Promise.all(
        ids.map(async (id) => {
          const cap = deptCaps.find((c) => c.id === Number(id));
          if (!cap) return;
          await updateDepartmentTermCapacity(Number(id), { capacity: numberOrNull(cap.capacity) });
        })
      );
      showMessage('病院×科×タームの定員を保存しました。', 'success');
      await loadData();
    } catch (err) {
      console.error(err);
      showMessage('保存に失敗しました。', 'error');
    } finally {
      setSavingDept(false);
    }
  };

  const createNewInstitution = async () => {
    if (!newInstitution.name.trim()) {
      showMessage('病院名を入力してください。', 'error');
      return;
    }
    try {
      const payload = {
        name: newInstitution.name.trim(),
        short_name: newInstitution.short_name.trim() || newInstitution.name.trim(),
        is_rural: Boolean(newInstitution.is_rural),
      };
      await createInstitution(payload);
      showMessage('病院を登録しました。', 'success');
      setNewInstitution({ name: '', short_name: '', is_rural: false });
      await loadData();
    } catch (err) {
      console.error(err);
      showMessage('病院の登録に失敗しました。', 'error');
    }
  };

  const addTermBatchRow = () => {
    setNewTermBatch((prev) => [...prev, { id: Date.now().toString(), institution_id: '', caps: {} }]);
  };

  const removeTermBatchRow = (id) => {
    setNewTermBatch((prev) => prev.filter((r) => r.id !== id));
  };

  const updateTermBatchRow = (id, field, value) => {
    setNewTermBatch((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        if (field === 'institution_id') {
          return { ...r, [field]: value };
        }
        return r;
      })
    );
  };

  const updateTermBatchCap = (rowId, termId, field, value) => {
    setNewTermBatch((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const currentTermCaps = r.caps[termId] || {};
        return {
          ...r,
          caps: {
            ...r.caps,
            [termId]: { ...currentTermCaps, [field]: value },
          },
        };
      })
    );
  };

  const registerTermBatch = async () => {
    const payload = [];
    for (const row of newTermBatch) {
      if (!row.institution_id) continue;
      for (const term of terms) {
        const cap = row.caps[term.id];
        if (cap) {
          // Check if at least one field is filled
          const hasValue =
            cap.total_capacity || cap.male_capacity || cap.female_capacity || cap.stay_capacity;
          if (hasValue) {
            payload.push({
              institution_id: Number(row.institution_id),
              term_id: term.id,
              total_capacity: numberOrNull(cap.total_capacity),
              male_capacity: numberOrNull(cap.male_capacity),
              female_capacity: numberOrNull(cap.female_capacity),
              stay_capacity: numberOrNull(cap.stay_capacity),
            });
          }
        }
      }
    }

    if (payload.length === 0) {
      showMessage('登録するデータがありません（病院・定員を入力してください）', 'error');
      return;
    }

    setLoading(true);
    try {
      await Promise.all(payload.map((p) => createTermCapacity(p)));
      showMessage(`${payload.length}件のデータを登録しました`, 'success');
      setNewTermBatch([{ id: Date.now().toString(), institution_id: '', caps: {} }]);
      await loadData();
    } catch (err) {
      console.error(err);
      showMessage('登録に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const addBatchRow = () => {
    setNewDeptBatch((prev) => [
      ...prev,
      { id: Date.now().toString(), institution_id: '', department_id: '', caps: {} },
    ]);
  };

  const removeBatchRow = (id) => {
    setNewDeptBatch((prev) => prev.filter((r) => r.id !== id));
  };

  const updateBatchRow = (id, field, value) => {
    setNewDeptBatch((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        if (field === 'institution_id' || field === 'department_id') {
          return { ...r, [field]: value };
        }
        return r;
      })
    );
  };

  const updateBatchCap = (rowId, termId, value) => {
    setNewDeptBatch((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        return {
          ...r,
          caps: { ...r.caps, [termId]: value },
        };
      })
    );
  };

  const registerBatch = async () => {
    const payload = [];
    for (const row of newDeptBatch) {
      if (!row.institution_id || !row.department_id) continue;
      for (const term of terms) {
        const val = row.caps[term.id];
        if (val !== undefined && val !== '') {
          payload.push({
            institution_id: Number(row.institution_id),
            department_id: Number(row.department_id),
            term_id: term.id,
            capacity: numberOrNull(val),
          });
        }
      }
    }

    if (payload.length === 0) {
      showMessage('登録するデータがありません（病院・診療科・定員を入力してください）', 'error');
      return;
    }

    setLoading(true);
    try {
      await Promise.all(payload.map((p) => createDepartmentTermCapacity(p)));
      showMessage(`${payload.length}件のデータを登録しました`, 'success');
      setNewDeptBatch([{ id: Date.now().toString(), institution_id: '', department_id: '', caps: {} }]);
      await loadData();
    } catch (err) {
      console.error(err);
      showMessage('登録に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-900">
        <div className="text-sm text-slate-600">読み込み中...</div>
      </div>
    );
  }

  const alertTone = message
    ? {
      success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      info: 'bg-blue-50 border-blue-200 text-blue-800',
    }[message.tone] || 'bg-blue-50 border-blue-200 text-blue-800'
    : '';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 relative">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50 cursor-wait">
          <div className="text-sm font-medium text-slate-900 bg-white px-4 py-2 rounded shadow-lg border border-slate-200">
            処理中...
          </div>
        </div>
      )}
      <div className="max-w-screen-2xl mx-auto px-8 py-10 space-y-8">
        <header className="flex items-center gap-4">
          <Link
            to="/admin-dashboard"
            className="p-2 -ml-2 rounded-lg hover:bg-slate-200 text-slate-600 transition-colors"
            aria-label="ダッシュボードに戻る"
          >
            <ArrowLeft size={28} />
          </Link>
          <h1 className="text-3xl font-bold">病院・科・ターム定員管理</h1>
        </header>

        {message && <div className={`rounded-md border px-4 py-3 text-sm font-medium ${alertTone}`}>{message.text}</div>}

        <div className="flex border-b border-slate-200">
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'term'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            onClick={() => setActiveTab('term')}
          >
            病院・ターム定員
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'dept'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            onClick={() => setActiveTab('dept')}
          >
            診療科詳細定員
          </button>
        </div>

        {activeTab === 'term' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            <section className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">病院の新規登録</h2>
                    <p className="text-sm text-slate-600">病院名 / 略称 / 地域枠指定 を指定して追加します。</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-slate-700">病院名 (name)</label>
                    <input
                      type="text"
                      value={newInstitution.name}
                      onChange={(e) => setNewInstitution((prev) => ({ ...prev, name: e.target.value }))}
                      className="rounded-md border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-slate-700">略称 (short_name)</label>
                    <input
                      type="text"
                      value={newInstitution.short_name}
                      onChange={(e) => setNewInstitution((prev) => ({ ...prev, short_name: e.target.value }))}
                      className="rounded-md border border-slate-300 px-3 py-2"
                      placeholder="未入力なら name を流用"
                    />
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={newInstitution.is_rural}
                      onChange={(e) => setNewInstitution((prev) => ({ ...prev, is_rural: e.target.checked }))}
                    />
                    地域実習枠指定であれば選択してください。
                  </label>
                  <div className="pt-2">
                    <Button className="bg-slate-900 text-white" onClick={createNewInstitution}>
                      病院を新規追加
                    </Button>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-6 space-y-4 lg:col-span-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">病院 × タームの新規登録</h2>
                    <p className="text-sm text-slate-600">
                      病院を選択し、各タームの定員（総枠/男/女/泊）を入力してください。複数の病院を一括登録できます。
                    </p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm bg-white border border-slate-200 rounded-lg">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-slate-600">
                        <th className="px-3 py-2 whitespace-nowrap">病院</th>
                        {terms
                          .slice()
                          .sort((a, b) => a.id - b.id)
                          .map((term) => (
                            <th key={term.id} className="px-3 py-2 whitespace-nowrap min-w-[140px]">
                              {term.name && !/^\d+$/.test(term.name) ? term.name : `ターム${term.name || term.id}`}
                              <div className="text-[10px] font-normal text-slate-400">総 / 男 / 女 / 泊</div>
                            </th>
                          ))}
                        <th className="px-3 py-2 whitespace-nowrap">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {newTermBatch.map((row) => (
                        <tr key={row.id}>
                          <td className="px-3 py-2 align-top">
                            <select
                              value={row.institution_id}
                              onChange={(e) => updateTermBatchRow(row.id, 'institution_id', e.target.value)}
                              className="w-40 rounded-md border border-slate-300 px-2 py-1 bg-white"
                            >
                              <option value="">選択</option>
                              {institutions
                                .slice()
                                .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'))
                                .map((inst) => (
                                  <option key={inst.id} value={inst.id}>
                                    {inst.name}
                                  </option>
                                ))}
                            </select>
                          </td>
                          {terms
                            .slice()
                            .sort((a, b) => a.id - b.id)
                            .map((term) => {
                              const cap = row.caps[term.id] || {};
                              return (
                                <td key={term.id} className="px-3 py-2 align-top">
                                  <div className="grid grid-cols-2 gap-1">
                                    <input
                                      type="number"
                                      placeholder="総"
                                      className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs"
                                      value={cap.total_capacity ?? ''}
                                      onChange={(e) =>
                                        updateTermBatchCap(row.id, term.id, 'total_capacity', e.target.value)
                                      }
                                    />
                                    <input
                                      type="number"
                                      placeholder="男"
                                      className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs"
                                      value={cap.male_capacity ?? ''}
                                      onChange={(e) =>
                                        updateTermBatchCap(row.id, term.id, 'male_capacity', e.target.value)
                                      }
                                    />
                                    <input
                                      type="number"
                                      placeholder="女"
                                      className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs"
                                      value={cap.female_capacity ?? ''}
                                      onChange={(e) =>
                                        updateTermBatchCap(row.id, term.id, 'female_capacity', e.target.value)
                                      }
                                    />
                                    <input
                                      type="number"
                                      placeholder="泊"
                                      className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs"
                                      value={cap.stay_capacity ?? ''}
                                      onChange={(e) =>
                                        updateTermBatchCap(row.id, term.id, 'stay_capacity', e.target.value)
                                      }
                                    />
                                  </div>
                                </td>
                              );
                            })}
                          <td className="px-3 py-2 align-top">
                            {newTermBatch.length > 1 && (
                              <button
                                onClick={() => removeTermBatchRow(row.id)}
                                className="text-red-600 hover:text-red-800 text-xs underline"
                              >
                                削除
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={addTermBatchRow}>
                    + 行を追加
                  </Button>
                  <Button className="bg-slate-900 text-white" onClick={registerTermBatch}>
                    一括登録を実行
                  </Button>
                </div>
              </div>
            </section>
            <section className="rounded-xl bg-white border border-slate-200 shadow-sm p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-semibold">病院 × ターム</h2>
                </div>
                <div className="flex gap-2">
                  {checkedTermRows.size > 0 && (
                    <Button
                      variant="destructive"
                      className="bg-red-600 text-white hover:bg-red-700"
                      onClick={handleBulkDeleteTermRows}
                    >
                      選択した {checkedTermRows.size} 件を削除
                    </Button>
                  )}
                  <Button className="bg-slate-900 text-white" onClick={saveTerms} disabled={savingTerm}>
                    {savingTerm ? '保存中...' : '変更を保存'}
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm bg-white border border-slate-200 rounded-lg">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-slate-600">
                      <th className="px-3 py-2 w-10"></th>
                      <th className="px-3 py-2">病院名</th>
                      <th className="px-3 py-2">ターム</th>
                      <th className="px-3 py-2">総枠</th>
                      <th className="px-3 py-2">男性枠</th>
                      <th className="px-3 py-2">女性枠</th>
                      <th className="px-3 py-2">宿泊枠</th>
                      <th className="px-3 py-2 w-16 text-center">同性限</th>
                      <th className="px-3 py-2 w-16">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {termRows.map((cap, idx) => {
                      const isChecked = checkedTermRows.has(cap.id);
                      const dirty = Boolean(termDrafts[cap.id]);
                      let rowClass = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                      if (isChecked) rowClass = 'bg-blue-50';
                      if (dirty) rowClass = 'bg-emerald-50';

                      return (
                        <tr
                          key={cap.id}
                          className={`${rowClass} cursor-pointer hover:bg-slate-100 transition-colors`}
                          onClick={() => toggleTermRowCheck(cap.id)}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleTermRowCheck(cap.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded border-slate-300"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap font-medium">{getInstitutionName(cap.institution_id) || '-'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{termMap[cap.term_id]?.name || `ターム${cap.term_id}`}</td>
                          {['total_capacity', 'male_capacity', 'female_capacity', 'stay_capacity'].map((field) => (
                            <td key={field} className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="number"
                                max={999}
                                value={cap[field] ?? ''}
                                onChange={(e) => handleTermDraft(cap.id, field, e.target.value)}
                                className="w-24 rounded-md border border-slate-300 px-2 py-1"
                              />
                            </td>
                          ))}
                          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={cap.is_same_sex_only ?? false}
                              onChange={(e) => handleTermDraft(cap.id, 'is_same_sex_only', e.target.checked)}
                              className="rounded border-slate-300"
                            />
                          </td>
                          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleDeleteTermRow(cap.id)}
                              className="text-red-600 hover:text-red-800 text-xs underline whitespace-nowrap"
                            >
                              削除
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'dept' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            <section className="rounded-xl bg-white border border-slate-200 shadow-sm p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">病院 × 科 × タームの新規登録</h2>
                  <p className="text-sm text-slate-600">
                    病院・診療科を選択し、各タームの定員を入力してください。複数の組み合わせを一括登録できます。
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm bg-white border border-slate-200 rounded-lg">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-slate-600">
                      <th className="px-3 py-2 whitespace-nowrap">病院</th>
                      <th className="px-3 py-2 whitespace-nowrap">診療科</th>
                      {terms
                        .slice()
                        .sort((a, b) => a.id - b.id)
                        .map((term) => (
                          <th key={term.id} className="px-3 py-2 whitespace-nowrap min-w-[60px]">
                            {term.name && !/^\d+$/.test(term.name) ? term.name : `ターム${term.name || term.id}`}
                          </th>
                        ))}
                      <th className="px-3 py-2 whitespace-nowrap">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {newDeptBatch.map((row) => (
                      <tr key={row.id}>
                        <td className="px-3 py-2">
                          <select
                            value={row.institution_id}
                            onChange={(e) => updateBatchRow(row.id, 'institution_id', e.target.value)}
                            className="w-40 rounded-md border border-slate-300 px-2 py-1 bg-white"
                          >
                            <option value="">選択</option>
                            {institutions
                              .slice()
                              .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'))
                              .map((inst) => (
                                <option key={inst.id} value={inst.id}>
                                  {inst.name}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={row.department_id}
                            onChange={(e) => updateBatchRow(row.id, 'department_id', e.target.value)}
                            className="w-40 rounded-md border border-slate-300 px-2 py-1 bg-white"
                          >
                            <option value="">選択</option>
                            {departments
                              .slice()
                              .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'))
                              .map((dept) => (
                                <option key={dept.id} value={dept.id}>
                                  {dept.name}
                                </option>
                              ))}
                          </select>
                        </td>
                        {terms
                          .slice()
                          .sort((a, b) => a.id - b.id)
                          .map((term) => (
                            <td key={term.id} className="px-3 py-2">
                              <input
                                type="number"
                                max={999}
                                value={row.caps[term.id] ?? ''}
                                onChange={(e) => updateBatchCap(row.id, term.id, e.target.value)}
                                className="w-full min-w-[50px] rounded-md border border-slate-300 px-2 py-1 text-center"
                                placeholder="-"
                              />
                            </td>
                          ))}
                        <td className="px-3 py-2">
                          {newDeptBatch.length > 1 && (
                            <button
                              onClick={() => removeBatchRow(row.id)}
                              className="text-red-600 hover:text-red-800 text-xs underline"
                            >
                              削除
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={addBatchRow}>
                  + 行を追加
                </Button>
                <Button className="bg-slate-900 text-white" onClick={registerBatch}>
                  一括登録を実行
                </Button>
              </div>
            </section>
            <section className="rounded-xl bg-white border border-slate-200 shadow-sm p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-semibold">病院 × 科 × ターム</h2>
                  <p className="text-sm text-slate-600">既存レコードのみ編集できます。NULL は空欄、0 は 0 のまま扱います。</p>
                </div>
                <div className="flex gap-2">
                  {checkedDeptRows.size > 0 && (
                    <Button
                      variant="destructive"
                      className="bg-red-600 text-white hover:bg-red-700"
                      onClick={handleBulkDeleteDeptRows}
                    >
                      選択した {checkedDeptRows.size} 行を削除
                    </Button>
                  )}
                  <Button className="bg-slate-900 text-white" onClick={saveDept} disabled={savingDept}>
                    {savingDept ? '保存中...' : '変更を保存'}
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm bg-white border border-slate-200 rounded-lg">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-slate-600">
                      <th className="px-3 py-2 w-10"></th>
                      <th className="px-3 py-2 whitespace-nowrap">病院名</th>
                      <th className="px-3 py-2 whitespace-nowrap">診療科</th>
                      {terms
                        .slice()
                        .sort((a, b) => a.id - b.id)
                        .map((term) => (
                          <th key={term.id} className="px-3 py-2 whitespace-nowrap min-w-[80px]">
                            {term.name && !/^\d+$/.test(term.name) ? term.name : `ターム${term.name || term.id}`}
                          </th>
                        ))}
                      <th className="px-3 py-2 w-16">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {deptMatrix.map((row, idx) => {
                      const rowKey = `${row.institution_id}-${row.department_id}`;
                      const isChecked = checkedDeptRows.has(rowKey);
                      let rowClass = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                      if (isChecked) rowClass = 'bg-blue-50';

                      return (
                        <tr
                          key={rowKey}
                          className={`${rowClass} cursor-pointer hover:bg-slate-100 transition-colors`}
                          onClick={() => toggleDeptRowCheck(rowKey)}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleDeptRowCheck(rowKey)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded border-slate-300"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap font-medium">
                            {getInstitutionName(row.institution_id) || '-'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-slate-800">
                            {getDepartmentName(row.department_id) || '-'}
                          </td>
                          {terms
                            .slice()
                            .sort((a, b) => a.id - b.id)
                            .map((term) => {
                              const cap = row.caps[term.id];
                              if (!cap) {
                                return (
                                  <td key={term.id} className="px-3 py-2 text-center text-slate-400">
                                    -
                                  </td>
                                );
                              }
                              const dirty = Boolean(deptDrafts[cap.id]);
                              return (
                                <td
                                  key={term.id}
                                  className={`px-3 py-2 ${dirty ? 'bg-emerald-50' : ''}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="number"
                                    max={999}
                                    value={cap.capacity ?? ''}
                                    onChange={(e) => handleDeptDraft(cap.id, e.target.value)}
                                    className="w-full min-w-[60px] rounded-md border border-slate-300 px-2 py-1 text-center"
                                  />
                                </td>
                              );
                            })}
                          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleDeleteDeptRow(row)}
                              className="text-red-600 hover:text-red-800 text-xs underline whitespace-nowrap"
                            >
                              削除
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCapacityManager;
